import * as XLSX from 'xlsx';
import { ConvertApiResponse, ZoneMapping, RateSlab, RateBracket, OtherChargeItem } from './RateSheetPreview';

/**
 * Normalizes charge type to match Speedbox rate sheet schema ('percent', 'fixed', 'per kg')
 */
function normalizeChargeType(unit?: string, name?: string): 'percent' | 'fixed' | 'per kg' {
  const unitStr = (unit || '').toLowerCase();
  const nameStr = (name || '').toLowerCase();
  if (
    unitStr.includes('%') ||
    unitStr.includes('percent') ||
    nameStr.includes('%') ||
    nameStr.includes('percent') ||
    nameStr.includes('gst')
  ) {
    return 'percent';
  }
  if (unitStr.includes('kg') || unitStr.includes('/kg') || nameStr.includes('per kg') || nameStr.includes('/kg')) {
    return 'per kg';
  }
  return 'fixed';
}

/**
 * Exports converted rate sheet data into a 3-tab Excel (.xlsx) file matching the exact Speedbox Sample Rate Sheet schema:
 * - Sheet 1: 'zones' -> [Pincode, City, State, Country, Destination Code, Zone, Transit Time, Reputation]
 * - Sheet 2: 'rates' -> [Weight, Zone 1, Zone 2, ...]
 * - Sheet 3: 'charges' -> [Charge Name, Charge Value, Charge Type, Min, Max, Charges Applied, Zones]
 */
export function exportRateSheetToExcel(data: ConvertApiResponse, customFileName?: string): void {
  const normalizedData: ConvertApiResponse = data?.data && typeof data.data === 'object' ? data.data : data;
  const { vendor = 'Vendor', rateType = 'Parcel', zones = [], rates, otherCharges = [] } = normalizedData;

  const workbook = XLSX.utils.book_new();

  // ==========================================
  // 1. Sheet: zones (Sample Format)
  // Header: ['Pincode', 'City', 'State', 'Country', 'Destination Code', 'Zone', 'Transit Time', 'Reputation']
  // ==========================================
  const zoneHeaders = ['Pincode', 'City', 'State', 'Country', 'Destination Code', 'Zone', 'Transit Time', 'Reputation'];

  const zoneRows = zones.map((z: ZoneMapping) => [
    '', // Pincode
    '', // City
    '', // State
    z.country || z.vendorCountry || '',
    z.countryCode || '',
    z.zone !== undefined && z.zone !== null ? String(z.zone) : '',
    z.transitDays !== undefined && z.transitDays !== null ? String(z.transitDays) : '',
    '', // Reputation
  ]);

  const zonesWorksheet = XLSX.utils.aoa_to_sheet([zoneHeaders, ...zoneRows]);
  zonesWorksheet['!cols'] = [
    { wch: 10 }, // Pincode
    { wch: 12 }, // City
    { wch: 12 }, // State
    { wch: 30 }, // Country
    { wch: 18 }, // Destination Code
    { wch: 10 }, // Zone
    { wch: 14 }, // Transit Time
    { wch: 12 }, // Reputation
  ];
  XLSX.utils.book_append_sheet(workbook, zonesWorksheet, 'zones');

  // ==========================================
  // 2. Sheet: rates (Sample Format)
  // Header: ['Weight', '1', '2', '3', ...]
  // ==========================================
  const slabs: RateSlab[] = rates?.slabs || [];
  const brackets: RateBracket[] = rates?.brackets || [];

  // Extract all unique zones in sorted order
  const zoneSet = new Set<string>();
  zones.forEach(z => {
    if (z.zone !== undefined && z.zone !== null) zoneSet.add(String(z.zone));
  });
  slabs.forEach(s => {
    if (s.rates) Object.keys(s.rates).forEach(z => zoneSet.add(z));
  });
  brackets.forEach(b => {
    if (b.perKg) Object.keys(b.perKg).forEach(z => zoneSet.add(z));
  });

  const uniqueZones = Array.from(zoneSet).sort((a, b) => {
    const numA = Number(a);
    const numB = Number(b);
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  const rateHeaders = ['Weight', ...uniqueZones];
  const rateRows: (string | number)[][] = [];

  // Add slab rows
  slabs.forEach(slab => {
    const row: (string | number)[] = [slab.weightKg];
    uniqueZones.forEach(z => {
      const val = slab.rates?.[z];
      row.push(val !== undefined && val !== null ? val : '');
    });
    rateRows.push(row);
  });

  // Add bracket rows
  brackets.forEach(bracket => {
    const rangeLabel =
      bracket.weightTo > 50000 ? `${bracket.weightFrom}+` : `${bracket.weightFrom}-${bracket.weightTo}`;
    const row: (string | number)[] = [rangeLabel];
    uniqueZones.forEach(z => {
      const val = bracket.perKg?.[z];
      row.push(val !== undefined && val !== null ? val : '');
    });
    rateRows.push(row);
  });

  const ratesWorksheet = XLSX.utils.aoa_to_sheet([rateHeaders, ...rateRows]);
  ratesWorksheet['!cols'] = [
    { wch: 14 }, // Weight
    ...uniqueZones.map(() => ({ wch: 12 })), // Zone columns
  ];
  XLSX.utils.book_append_sheet(workbook, ratesWorksheet, 'rates');

  // ==========================================
  // 3. Sheet: charges (Sample Format)
  // Header: ['Charge Name', 'Charge Value', 'Charge Type', 'Min', 'Max', 'Charges Applied', 'Zones']
  // ==========================================
  const chargeHeaders = ['Charge Name', 'Charge Value', 'Charge Type', 'Min', 'Max', 'Charges Applied', 'Zones'];

  const allZonesStr = uniqueZones.join(',');

  const chargeRows: (string | number)[][] = (otherCharges || []).map((ch: OtherChargeItem) => {
    let chargeVal: number | string = '';
    if (typeof ch.charge === 'number') {
      chargeVal = ch.charge;
    } else if (typeof ch.charge === 'string') {
      const parsed = parseFloat(ch.charge);
      chargeVal = isNaN(parsed) ? ch.charge : parsed;
    }

    const chargeTypeStr = normalizeChargeType(ch.unit, ch.name);

    return [
      ch.name || 'Charge',
      chargeVal,
      chargeTypeStr,
      '', // Min
      '', // Max
      'Freight', // Charges Applied
      allZonesStr, // Zones
    ];
  });

  if (chargeRows.length === 0) {
    chargeRows.push(['Emergency Situation Surcharge', 0, 'percent', '', '', 'Freight', allZonesStr]);
  }

  const chargesWorksheet = XLSX.utils.aoa_to_sheet([chargeHeaders, ...chargeRows]);
  chargesWorksheet['!cols'] = [
    { wch: 30 }, // Charge Name
    { wch: 14 }, // Charge Value
    { wch: 14 }, // Charge Type
    { wch: 10 }, // Min
    { wch: 10 }, // Max
    { wch: 16 }, // Charges Applied
    { wch: 30 }, // Zones
  ];
  XLSX.utils.book_append_sheet(workbook, chargesWorksheet, 'charges');

  // ==========================================
  // Generate File & Trigger Browser Download
  // ==========================================
  const sanitizedVendor = (vendor || 'RateSheet').replace(/[^a-zA-Z0-9_-]/g, '_');
  const sanitizedRateType = (rateType || 'Rates').replace(/[^a-zA-Z0-9_-]/g, '_');
  const defaultFileName = `${sanitizedVendor}_${sanitizedRateType}_Rate_Sheet.xlsx`;
  const fileName = customFileName
    ? customFileName.endsWith('.xlsx')
      ? customFileName
      : `${customFileName}.xlsx`
    : defaultFileName;

  XLSX.writeFile(workbook, fileName, { compression: true });
}

export default exportRateSheetToExcel;
